<template>
  <v-btn color="success" @click="exportPDF" :loading="isExporting">
    <v-icon left>mdi-file-pdf-box</v-icon> PDF
  </v-btn>
</template>

<script>
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

export default {
  name: "PDFExportComponent",
  props: {
    reportData: {
      type: Array,
      required: true
    },
    reportTitle: {
      type: String,
      required: true
    },
    propertyName: {
      type: String,
      required: true
    },
    startDate: {
      type: String,
      required: true
    },
    endDate: {
      type: String,
      required: true
    },
    logoBase64: {
      type: String,
      required: true
    },
    visibleColumns: {
      type: Object,
      required: true
    },
    sumField: {
      type: Function,
      required: true
    },
    formatCurrency: {
      type: Function,
      required: true
    },
    formatDate: {
      type: Function,
      required: true
    },
    columnDefinitions: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      isExporting: false
    };
  },
  methods: {
    async exportPDF() {
      this.isExporting = true;
      
      try {
        // Define the Roboto font
        const fonts = {
          Roboto: {
            normal: 'assets/Roboto/static/Roboto-Regular.ttf',
            bold: 'assets/Roboto/static/Roboto-Bold.ttf',
            italics: 'assets/Roboto/static/Roboto-Italic.ttf',
            bolditalics: 'assets/Roboto/static/Roboto-BoldItalic.ttf'
          }
        };

        pdfMake.fonts = fonts;

        // Prepare column headers based on visible columns
        const columnHeaders = this.columnDefinitions
          .filter(col => this.visibleColumns[col.field] || col.alwaysVisible)
          .map(col => ({ text: col.header, style: 'tableHeader' }));

        // Prepare data rows
        const tableBody = [columnHeaders];

        // Add regular data rows
        this.reportData.forEach(item => {
          const row = this.columnDefinitions
            .filter(col => this.visibleColumns[col.field] || col.alwaysVisible)
            .map(col => {
              const value = col.formatter ? col.formatter(item[col.field]) : item[col.field];
              return { text: value, style: 'tableData' };
            });
          
          tableBody.push(row);
        });

        // Add totals row if needed
        if (this.showTotals) {
          const totalsRow = this.columnDefinitions
            .filter(col => this.visibleColumns[col.field] || col.alwaysVisible)
            .map(col => {
              if (col.isTotalable) {
                return { text: this.formatCurrency(this.sumField(col.field)), style: 'tableFooter' };
              }
              return { text: col.field === 'reference' ? 'TOTALS' : '', style: 'tableFooter' };
            });
          
          tableBody.push(totalsRow);
        }

        // Calculate column widths
        const columnCount = columnHeaders.length;
        const columnWidth = 100 / columnCount;
        const columnWidths = Array(columnCount).fill(`${columnWidth}%`);

        const docDefinition = {
          defaultStyle: {
            font: 'Roboto'
          },
          content: [
            this.generateHeaderSection(),
            this.generateReportInfoSection(),
            {
              table: {
                headerRows: 1,
                widths: columnWidths,
                body: tableBody
              },
              layout: this.getTableLayout(),
              margin: [0, 0, 0, 20]
            },
            this.generateSummarySection()
          ],
          footer: this.generateFooter(),
          styles: this.getDocumentStyles(),
          pageMargins: [40, 60, 40, 60],
          pageSize: 'A4',
          pageOrientation: 'landscape'
        };

        pdfMake.createPdf(docDefinition).open();
      } catch (error) {
        console.error("Error generating PDF:", error);
        this.$emit('error', "Failed to generate PDF report");
      } finally {
        this.isExporting = false;
      }
    },
    
    generateHeaderSection() {
      return {
        stack: [
          {
            canvas: [
              {
                type: 'rect',
                x: 0,
                y: 0,
                w: 420,
                h: 180,
                color: 'black',
              },
            ],
            margin: [10, 10, 10, 10],
            alignment: 'center',
            image: this.logoBase64,
            width: 400,
            alignment: 'center',
            margin: [0, 20, 0, 20]
          },
          {
            text: this.reportTitle.toUpperCase(),
            style: 'header',
            alignment: 'center',
            margin: [0, 0, 0, 10]
          }
        ],
        alignment: 'center',
      };
    },
    
    generateReportInfoSection() {
      return {
        columns: [
          {
            width: '50%',
            stack: [
              { text: `Property: ${this.propertyName}`, style: 'propertyInfo' },
              { text: `Report Type: ${this.reportTitle}`, style: 'propertyInfo' }
            ]
          },
          {
            width: '50%',
            stack: [
              { text: `Date Range: ${this.formatDate(this.startDate)} to ${this.formatDate(this.endDate)}`, 
                style: 'propertyInfo', 
                alignment: 'right' },
              { text: `Generated: ${new Date().toLocaleDateString()}`, 
                style: 'propertyInfo', 
                alignment: 'right' }
            ]
          }
        ],
        margin: [0, 0, 0, 20]
      };
    },
    
    generateSummarySection() {
      return {
        text: 'Summary',
        style: 'sectionHeader',
        margin: [0, 0, 0, 10]
      };
    },
    
    getTableLayout() {
      return {
        fillColor: function (rowIndex, node, columnIndex) {
          return (rowIndex === 0) ? '#F1C284' : null;
        },
        hLineColor: function (i, node) { return '#CCCCCC'; },
        vLineColor: function (i, node) { return '#CCCCCC'; }
      };
    },
    
    generateFooter(currentPage, pageCount) {
      return {
        stack: [
          {
            canvas: [
              {
                type: 'line',
                x1: 0,
                y1: 0,
                x2: 515,
                y2: 0,
                lineWidth: 1,
                lineColor: '#000000',
              },
            ],
            margin: [0, 0, 0, 5],
          },
          {
            text: 'GQ Property Group | 33 Donkin Street Central Port Elizabeth Eastern Cape\n0615175157 | rent@gqproperty.co.za',
            style: 'footer',
            alignment: 'center',
          },
          {
            text: `Page ${currentPage} of ${pageCount}`,
            fontSize: 8,
            alignment: 'right',
            margin: [0, 5, 10, 0],
          },
        ],
      };
    },
    
    getDocumentStyles() {
      return {
        header: { fontSize: 18, bold: true, color: '#000080', margin: [0, 0, 0, 10] },
        subheader: { fontSize: 16, bold: true, color: '#F1C284' },
        propertyInfo: { fontSize: 10, margin: [0, 0, 0, 5] },
        sectionHeader: { fontSize: 14, bold: true, color: '#000080', margin: [0, 0, 0, 10] },
        tableHeader: { bold: true, fontSize: 10, color: '#FFFFFF', alignment: 'center' },
        tableData: { fontSize: 8, alignment: 'center' },
        tableFooter: { fontSize: 8, bold: true, fillColor: '#E0E0E0', alignment: 'center' },
        summaryLabel: { fontSize: 10, bold: true },
        summaryValue: { fontSize: 10, bold: true, alignment: 'right' },
        footer: { fontSize: 10 }
      };
    }
  }
};
</script>